import { PageData, PageDataParams } from './page-data';

import {
  DiscourseLine,
  PageType,
  TagResult,
  Tags,
  RawDiscoursePageData,
  DiscourseAnnotation,
  DiscourseAnswers,
  LastElement,
  Tag,
  DiscourseCustomFeedback,
} from '../common/page-data';
import { generateAnnotationOptions } from '../problem-generator/discourse-options';
import { MersenneTwister } from '../utils/mersenne-twister';

export class DiscourseData extends PageData {
  protected _content: DiscourseLine[];
  protected _annotations: DiscourseAnnotation[];
  protected _answers: DiscourseAnswers = {};

  protected _tags: Tags;
  protected _annotationOptions: Record<string, Tag[]>;

  public get content(): DiscourseLine[] {
    return this._content;
  }
  public get annotations(): DiscourseAnnotation[] {
    return this._annotations;
  }

  public get annotationOptions() {
    return this._annotationOptions;
  }

  public get tags(): Tags {
    return this._tags;
  }
  public get pageType(): PageType {
    return PageType.Problem;
  }
  public get answers(): DiscourseAnswers {
    return { ...this._answers };
  }
  public set answers(answers: DiscourseAnswers) {
    this._answers = answers;
  }

  public get question(): string {
    return this._question as string;
  }

  public get score(): number {
    const annotations = this.annotations;
    const score = annotations.reduce((result, annotation): number => {
      const answer = this.answers[annotation.id];
      const correct = !!answer && answer === annotation.tag;
      return correct ? ++result : result;
    }, 0);

    return score / annotations.length;
  }

  public get tagResults(): TagResult[] {
    const tagResults: TagResult[] = [];
    const annotations = this.annotations;

    annotations.forEach((annotation) => {
      const answer = this.answers[annotation.id];
      if (answer) {
        const correct = !!answer && answer === annotation.tag;
        const tag = this.tags[answer];
        tagResults.push({ tagId: answer, name: tag?.name || '', correct });
      }
    });
    return tagResults;
  }

  public get rawData(): RawDiscoursePageData {
    const playStats = this.playStats;

    const raw: RawDiscoursePageData = {
      ...(this._rawPageData as RawDiscoursePageData),
      answers: this.answers,
      playStats,
    };

    return raw;
  }

  public get annotationCustomFeedback() {
    return (this._rawPageData as RawDiscoursePageData).annotationIdToCustomFeedback;
  }

  public reset(): void {
    this.answers = {};
  }

  public constructor(params: PageDataParams) {
    super(params);

    const { rawPageData } = params;
    const { content, annotations, tags, answers, annotationOptions } =
      rawPageData as RawDiscoursePageData;
    this._content = content;
    this._annotations = annotations;
    this._tags = tags;
    this._answers = answers || {};
    this._annotationOptions = {};

    // For backwards compatibility, if raw data doesn't contain annotation options,
    // generate them here.
    const annotationOptionsToUse = annotationOptions ?? generateAnnotationOptions(
      new MersenneTwister(), annotations, tags);
    for (const annotationId in annotationOptionsToUse) {
      const tagsIds = annotationOptionsToUse[annotationId];
      this._annotationOptions[annotationId] = tagsIds.map(tagId => tags[tagId]).filter(v => v);
    }

    // mark the last node
    if (this._content.length > 0) {
      const item = this._content[this._content.length - 1];
      if (!item.element) {
        item.element = { last: true };
      } else if (!(item.element as LastElement).last) {
        (item.element as LastElement).last = true;
      }
    }
  }
}
